import { apiClient } from '../api-client';
import type { ApiResponse } from '@/types/api';
import type { InventoryItem, InventoryQueryParams, PagedInventoryResponse, InventoryMovement, BulkSettableStatus, BulkSetStatusResult } from '@/types/inventory';

export const inventoryApi = {
  getInventory: async (params?: InventoryQueryParams) => {
    const response = await apiClient.get<ApiResponse<PagedInventoryResponse>>('/inventory', { params });
    return response.data.data;
  },

  getById: async (id: number) => {
    const response = await apiClient.get<ApiResponse<InventoryItem>>(`/inventory/${id}`);
    return response.data.data;
  },

  getMovements: async (id: number) => {
    const response = await apiClient.get<ApiResponse<InventoryMovement[]>>(`/inventory/${id}/movements`);
    return response.data.data;
  },

  getNeedsHome: async () => {
    const response = await apiClient.get<ApiResponse<InventoryItem[]>>('/inventory/needs-home');
    return response.data.data;
  },

  assignLocation: async (id: number, locationId: number, timestamp?: string) => {
    const response = await apiClient.post<ApiResponse<InventoryItem>>(`/inventory/${id}/assign-location`, {
      locationId,
      timestamp,
    });
    return response.data.data;
  },

  relocate: async (id: number, toLocationId: number, notes?: string) => {
    const response = await apiClient.post<ApiResponse<InventoryItem>>(`/inventory/${id}/relocate`, {
      toLocationId,
      notes,
    });
    return response.data.data;
  },

  bulkAssignSort: async (inventoryItemIds: number[], sortId1?: string, sortId2?: string) => {
    await apiClient.put('/inventory/bulk-assign-sort', {
      inventoryItemIds,
      sortId1,
      sortId2,
    });
  },

  resequence: async (items: Array<{ inventoryItemId: number; newSequence: number }>) => {
    await apiClient.put('/inventory/resequence', { items });
  },

  removeItem: async (id: number) => {
    await apiClient.post(`/inventory/${id}/remove`);
  },

  bulkRemove: async (inventoryItemIds: number[]) => {
    await apiClient.put('/inventory/bulk-remove', { inventoryItemIds });
  },

  clearLocation: async (id: number) => {
    await apiClient.post(`/inventory/${id}/clear-location`);
  },

  bulkClearLocation: async (inventoryItemIds: number[]) => {
    await apiClient.put('/inventory/bulk-clear-location', { inventoryItemIds });
  },

  // Resolves to the server's summary message (e.g. how many items changed) so callers can show it.
  bulkSetStatus: async (inventoryItemIds: number[], status: BulkSettableStatus) => {
    const response = await apiClient.put<ApiResponse<BulkSetStatusResult>>('/inventory/bulk-set-status', { inventoryItemIds, status });
    return { message: response.data.message, skippedCount: response.data.data?.skippedCount ?? 0 };
  },

  deleteItem: async (id: number) => {
    await apiClient.delete(`/inventory/${id}`);
  },

  restoreItem: async (id: number) => {
    await apiClient.post(`/inventory/${id}/restore`);
  },

  updateTimestamp: async (id: number, locationAssignedAt: string | null) => {
    await apiClient.put(`/inventory/${id}/timestamp`, { locationAssignedAt });
  },
};
